import { Feature, LineString, Polygon, Position } from "geojson";
import { HlaWay, HlaWayType } from "../../../../hla/models/HlaWay";
import turf from "../../../../shared/utils/turf";
import { roadAndPathLayers } from "../../../utils/mapbox";
import { RootStore } from "../../../../shared/stores/RootStore";
import { importHlaUtils } from "../utils";
import { toast, toaster } from "../../../../shared/views/CustomToaster";
import { createWayFromLineFeature } from "../shared/createWayFromLineFeature";
import { calculateGeographicHeading } from "../../../utils/map-utils";

const radiusToleranceMeters = 5;
const headingToleranceDegrees = 20;

export async function importHlaFromStreetview(
  position: Position,
  heading: number,
  rootStore: RootStore,
  map: mapboxgl.Map,
  rightHandTraffic: boolean,
): Promise<HlaWay[]> {
  const { mapQueryStore, hlaStore, surveyStore, mapStore } = rootStore;
  const point = turf.point(position);

  importHlaUtils.centerMapAround(point, map);

  const buffer = turf.buffer(point, radiusToleranceMeters, {
    units: "meters",
  }) as Feature<Polygon>;
  const bbox = importHlaUtils.polygonToBbox(buffer, mapStore.map!);

  const features: Feature<LineString>[] = importHlaUtils
    .flattenLineAndMultiLineFeatures(
      mapStore.map!.queryRenderedFeatures(bbox, {
        layers: roadAndPathLayers,
      }),
    )
    .filter((feat) => turf.booleanIntersects(feat, buffer))
    .filter((feat) => {
      const segments = importHlaUtils.intersectLineAndPolygon(feat, buffer);

      if (!segments.length) return false;

      const intersectionHeading = calculateGeographicHeading(
        segments[0].geometry.coordinates[0],
        segments[0].geometry.coordinates[1],
      );

      return importHlaUtils.anglesEqualDirectionless(
        heading,
        intersectionHeading,
        headingToleranceDegrees,
      );
    });

  let featureCounter = 0;
  let wayCounter = 0;

  const toastId = toast("loading", `Querying current position. Please wait...`);

  const survey = surveyStore.getSelected()!;
  const enabledCategories = Object.values(HlaWayType);
  const importedWays: HlaWay[] = [];

  for (const feature of features) {
    const way = await createWayFromLineFeature(
      feature.id as number,
      feature.geometry.coordinates,
      feature.properties,
      enabledCategories,
      map,
      rightHandTraffic,
    );
    if (way) {
      way.surveyId = survey.id;
      const newWay = await hlaStore.create(way);
      importedWays.push(newWay);
      // this.addRequestToMap(way);
      wayCounter++;
    }

    featureCounter++;
    toaster.update(toastId, {
      title: `Processing features (${featureCounter} of ${features.length})`,
    });
  }

  if (wayCounter > 0) {
    toaster.update(toastId, {
      type: "success",
      title: `Done! ${wayCounter} ways imported`,
    });
  } else {
    toaster.update(toastId, {
      type: "warning",
      title: `No ways were found in this location and angle...`,
    });
  }

  hlaStore.updateMapSource();
  mapQueryStore.deactivate();

  return importedWays;
}
